import {zipSync, strToU8} from 'fflate';
import type {WorkspaceAsset} from './model';
import {parseDeck} from './parser';
import {SharedWorkspace} from './shared-workspace';
import type {DeckContent} from './shared-deck';

const DB_NAME = 'slides2-assets';
const DB_VERSION = 1;
const ASSET_STORE = 'assets';

type AssetRecord = {
  id: string;
  deck: string;
  name: string;
  kind: 'image' | 'video';
  blob: Blob;
  sharedUrl?: string;
};

const LEADING_MEDIA_LINE =
  /^!\[[^\]\r\n]*\]\((?:(?:animation|video):)?[^)\r\n]+(?:\s+"[^"\r\n]*")?\)(?:\{[^}\r\n]*\})?$/;

export function recoverLeadingMediaPrefix(source: string): {
  source: string;
  recoveredMedia: number;
} {
  const delimiter = /^---[ \t]*\r?$/m.exec(source);
  if (!delimiter || delimiter.index === 0) return {source, recoveredMedia: 0};
  const lines = source
    .slice(0, delimiter.index)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length || !lines.every((line) => LEADING_MEDIA_LINE.test(line))) {
    return {source, recoveredMedia: 0};
  }
  return {
    source: source.slice(delimiter.index),
    recoveredMedia: lines.length,
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(ASSET_STORE)) {
        const store = db.createObjectStore(ASSET_STORE, {keyPath: 'id'});
        store.createIndex('deck', 'deck');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionResult<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ASSET_STORE, mode);
    const request = action(transaction.objectStore(ASSET_STORE));
    transaction.oncomplete = () => resolve(request.result);
    transaction.onabort = () => reject(transaction.error ?? new Error('Could not save media.'));
    request.onerror = () => reject(request.error);
  });
}

function download(filename: string, contents: Blob): void {
  const url = URL.createObjectURL(contents);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export class DeckWorkspace {
  readonly deckId: string;
  readonly baseline: string;
  readonly recoveredMedia: number;
  #source: string;
  readonly shared: SharedWorkspace;

  private constructor(
    deckId: string,
    baseline: string,
    source: string,
    shared: SharedWorkspace,
    recoveredMedia = 0,
  ) {
    this.deckId = deckId;
    this.baseline = baseline;
    this.#source = source;
    this.shared = shared;
    this.recoveredMedia = recoveredMedia;
  }

  static async open(deckId: string): Promise<DeckWorkspace> {
    const shared = new SharedWorkspace(deckId);
    const previous = shared.current;
    try {await shared.refresh();} catch {shared.connected = false;}
    const imported = localStorage.getItem(`slides2:import:${deckId}`);
    let baselineSource = shared.current?.original.source ?? imported;
    if (!baselineSource) {
      const response = await fetch(`/decks/${encodeURIComponent(deckId)}/deck.md`);
      if (!response.ok || response.headers.get('content-type')?.includes('text/html')) throw new Error(`Deck "${deckId}" was not found.`);
      baselineSource = await response.text();
    }
    const baselineRecovery = recoverLeadingMediaPrefix(baselineSource);
    if (baselineRecovery.recoveredMedia && imported) {
      localStorage.setItem(
        `slides2:recovery:${deckId}:import:${Date.now()}`,
        baselineSource,
      );
      localStorage.setItem(`slides2:import:${deckId}`, baselineRecovery.source);
    }
    const baseline = baselineRecovery.source;

    const draftKey = `slides2:draft:${deckId}`;
    const draft = localStorage.getItem(draftKey);
    const draftRecovery = recoverLeadingMediaPrefix(draft ?? baseline);
    if (draft && draftRecovery.recoveredMedia) {
      localStorage.setItem(
        `slides2:recovery:${deckId}:draft:${Date.now()}`,
        draft,
      );
      if (draftRecovery.source === baseline) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, draftRecovery.source);
    }
    const source = shared.chooseSource(draftRecovery.source === baseline && !draft ? null : draftRecovery.source, baseline, previous);
    const workspace = new DeckWorkspace(
      deckId,
      baseline,
      source,
      shared,
      Math.max(baselineRecovery.recoveredMedia, draftRecovery.recoveredMedia),
    );
    workspace.save(source);
    return workspace;
  }

  get source(): string {
    return this.#source;
  }

  get changed(): boolean {
    return this.#source !== this.baseline;
  }

  save(source: string): void {
    if (source === this.baseline) {
      localStorage.removeItem(`slides2:draft:${this.deckId}`);
    } else {
      localStorage.setItem(`slides2:draft:${this.deckId}`, source);
    }
    this.#source = source;
  }

  restoreBaseline(): void {
    this.save(this.baseline);
  }

  get baseDeckId(): string {return this.shared.baseDeckId;}

  assetUrl(source: string): string {
    if (!source.startsWith('assets/')) return source;
    return this.shared.assets[source.slice(7)]?.url ?? `/decks/${encodeURIComponent(this.baseDeckId)}/${source}`;
  }

  get saveStatus(): string {
    if (this.shared.localOnly) return 'Saved locally';
    if (this.shared.pending) return 'Saved locally · not online';
    if (this.shared.current) return `${this.shared.connected ? 'Saved online' : 'Offline copy'} · v${this.shared.baseRevision}`;
    return 'Original';
  }

  backup(source: string): void {
    const content: DeckContent = {source, assets: this.shared.assets, baseDeckId: this.baseDeckId};
    localStorage.setItem(`slides:recovery:${this.deckId}:${Date.now()}:${crypto.randomUUID()}`, JSON.stringify(content));
  }

  async saveOnline(source: string, copyId?: string): Promise<string> {
    parseDeck(source);
    const changed = this.source !== source;
    // Source + expected revision are one atomic localStorage value. A quota or
    // crash between later writes cannot make an unsent edit look acknowledged.
    if (!copyId || changed) this.shared.stage(source);
    this.save(source);
    if (this.shared.localOnly && !copyId) return this.deckId;
    const assets = {...this.shared.assets};
    for (const record of await this.localAssetRecords()) {
      // If another editor replaced this name, its cloud version wins unless the
      // user explicitly added a new local asset (unique names are enforced).
      if (assets[record.name] && record.sharedUrl !== assets[record.name].url) continue;
      const url = record.sharedUrl ?? await this.shared.upload(record.blob);
      record.sharedUrl = url;
      const db = await openDatabase();
      await transactionResult(db, 'readwrite', store => store.put(record));
      db.close();
      assets[record.name] = {url, kind: record.kind};
    }
    const content = {source, assets, baseDeckId: this.baseDeckId};
    const saved = await this.shared.save(content, copyId ? source : this.baseline, copyId);
    if (copyId) {
      const copy = new SharedWorkspace(copyId);
      copy.accept(saved);
      localStorage.setItem(`slides2:draft:${copyId}`, saved.source);
    }
    return saved.id;
  }

  /** Loading a version edits the working copy. Publishing it still requires Save. */
  useVersion(content: DeckContent): void {
    this.shared.assets = content.assets;
    this.shared.baseDeckId = content.baseDeckId;
    this.shared.stage(content.source);
    this.save(content.source);
  }

  async loadOnline(): Promise<string> {
    const remote = await this.shared.refresh();
    this.shared.accept(remote);
    this.save(remote?.source ?? this.baseline);
    return this.source;
  }

  async addAsset(
    file: File | Blob,
    preferredName?: string,
    options: {replace?: boolean} = {},
  ): Promise<WorkspaceAsset> {
    const extension = file.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'bin';
    const rawName = file instanceof File ? file.name : preferredName;
    const base = (rawName || `pasted-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`)
      .replace(/[^\p{Letter}\p{Number}._-]+/gu, '-')
      .replace(/^-+/, '');
    const name = options.replace ? base : await this.uniqueAssetName(base);
    const kind = file.type.startsWith('video/') ? 'video' : 'image';
    const record: AssetRecord = {
      id: `${this.deckId}:${name}`,
      deck: this.deckId,
      name,
      kind,
      blob: file,
    };
    const db = await openDatabase();
    await transactionResult(db, 'readwrite', (store) => store.put(record));
    db.close();
    return this.toWorkspaceAsset(record);
  }

  async assets(source = this.source): Promise<WorkspaceAsset[]> {
    const referenced = parseDeck(source).slides.flatMap((slide) => slide.media);
    const staticAssets = new Map<string, WorkspaceAsset>();
    for (const media of referenced) {
      if (!media.source.startsWith('assets/')) continue;
      const name = media.source.slice('assets/'.length);
      staticAssets.set(name, {
        id: `${this.deckId}:${name}`,
        name,
        kind: media.kind === 'video' ? 'video' : 'image',
        source: 'deck',
        url: this.assetUrl(media.source),
      });
    }

    const local = await this.localAssetRecords();
    for (const [name, asset] of Object.entries(this.shared.assets)) {
      staticAssets.set(name, {id: `${this.deckId}:${name}`, name, kind: asset.kind, source: 'shared', url: asset.url});
    }
    for (const record of local) {
      const cloud = this.shared.assets[record.name];
      if (!cloud || record.sharedUrl === cloud.url) staticAssets.set(record.name, this.toWorkspaceAsset(record));
    }
    return [...staticAssets.values()];
  }

  async removeAsset(name: string): Promise<void> {
    const db = await openDatabase();
    await transactionResult(db, 'readwrite', (store) =>
      store.delete(`${this.deckId}:${name}`),
    );
    db.close();
    delete this.shared.assets[name];
  }

  async exportMarkdown(source = this.source): Promise<void> {
    download(`${this.deckId}.md`, new Blob([source], {type: 'text/markdown'}));
  }

  async exportPackage(source = this.source): Promise<void> {
    const files: Record<string, Uint8Array> = {'deck.md': strToU8(source)};
    for (const asset of await this.assets(source)) {
      const response = asset.blob ? null : await fetch(asset.url);
      if (response && (!response.ok || response.headers.get('content-type')?.includes('text/html'))) {
        throw new Error(`Could not export ${asset.name}. Check the media file.`);
      }
      const bytes = asset.blob
        ? new Uint8Array(await asset.blob.arrayBuffer())
        : new Uint8Array(await response!.arrayBuffer());
      files[`assets/${asset.name}`] = bytes;
    }
    download(
      `${this.deckId}.slides.zip`,
      new Blob([zipSync(files, {level: 6}) as BlobPart], {type: 'application/zip'}),
    );
  }

  async exportOffline(source = this.source, progress: (name: string) => void = () => {}): Promise<void> {
    const {exportOffline} = await import('./export-offline');
    download(`${this.deckId}-offline.zip`, await exportOffline(this, source, progress));
  }

  async cacheOffline(): Promise<number> {
    const urls = [
      location.href,
      '/',
      '/index.html',
      '/manifest.webmanifest',
      `/decks/${encodeURIComponent(this.deckId)}/deck.md`,
      ...performance
        .getEntriesByType('resource')
        .map((entry) => entry.name)
        .filter((url) => url.startsWith(location.origin)),
      ...(await this.assets()).filter((asset) => !asset.blob).map((asset) => asset.url),
    ];
    const cache = await caches.open(`slides2-deck-${this.deckId}`);
    const unique = [...new Set(urls)];
    await Promise.all(unique.map((url) => cache.add(url)));
    return unique.length;
  }

  private async localAssetRecords(): Promise<AssetRecord[]> {
    const db = await openDatabase();
    const records = await new Promise<AssetRecord[]>((resolve, reject) => {
      const transaction = db.transaction(ASSET_STORE, 'readonly');
      const index = transaction.objectStore(ASSET_STORE).index('deck');
      const request = index.getAll(this.deckId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return records;
  }

  private async uniqueAssetName(preferred: string): Promise<string> {
    const used = new Set([
      ...Object.keys(this.shared.assets),
      ...parseDeck(this.source).slides.flatMap(slide => slide.media).filter(media => media.source.startsWith('assets/')).map(media => media.source.slice(7)),
      ...(await this.localAssetRecords()).map(asset => asset.name),
    ]);
    if (!used.has(preferred)) return preferred;
    const dot = preferred.lastIndexOf('.');
    const stem = dot > 0 ? preferred.slice(0, dot) : preferred;
    const extension = dot > 0 ? preferred.slice(dot) : '';
    let suffix = 2;
    while (used.has(`${stem}-${suffix}${extension}`)) suffix += 1;
    return `${stem}-${suffix}${extension}`;
  }

  private toWorkspaceAsset(record: AssetRecord): WorkspaceAsset {
    return {
      id: record.id,
      name: record.name,
      kind: record.kind,
      source: record.sharedUrl && this.shared.assets[record.name]?.url === record.sharedUrl ? 'shared' : 'local',
      blob: record.blob,
      url: URL.createObjectURL(record.blob),
    };
  }
}
